/** A tidy left-to-right tree: one column per depth, children stacked, every parent centred on its children. */
const COLS = [292, 216, 232, 256, 256, 256];
const COL_GAP = 60;
const ROW_GAP = 10;
const GROUP_GAP = 22;

export function sizeOf(n) {
  if (n.depth === 0) return [COLS[0], 84];
  if (n.children.length) return [COLS[Math.min(n.depth, COLS.length - 1)], 60];
  return [COLS[Math.min(n.depth, COLS.length - 1)], n.part || n.sub ? 60 : 44];
}

const xOf = (depth) => COLS.slice(0, depth).reduce((a, w) => a + w, 0) + depth * COL_GAP;

export function layoutTree(root, expanded) {
  const pos = {};
  const edges = [];
  const kidsOf = (n) => (expanded.has(n.id) ? n.children : []);
  const need = {};
  const measure = (n) => {
    const [, h] = sizeOf(n);
    const kids = kidsOf(n);
    if (!kids.length) { need[n.id] = h; return h; }
    let span = 0;
    kids.forEach((c, i) => { span += measure(c); if (i < kids.length - 1) span += c.children.length && kidsOf(c).length ? GROUP_GAP : ROW_GAP; });
    need[n.id] = Math.max(h, span);
    return need[n.id];
  };
  measure(root);
  const place = (n, top) => {
    const [w, h] = sizeOf(n);
    const total = need[n.id];
    pos[n.id] = { x: xOf(n.depth), y: top + (total - h) / 2, w, h };
    const kids = kidsOf(n);
    if (!kids.length) return;
    let span = 0;
    kids.forEach((c, i) => { span += need[c.id]; if (i < kids.length - 1) span += c.children.length && kidsOf(c).length ? GROUP_GAP : ROW_GAP; });
    let y = top + (total - span) / 2;
    kids.forEach((c, i) => {
      place(c, y);
      edges.push({ from: n.id, to: c.id });
      y += need[c.id] + (i < kids.length - 1 ? (c.children.length && kidsOf(c).length ? GROUP_GAP : ROW_GAP) : 0);
    });
  };
  place(root, 0);
  const all = Object.values(pos);
  const bbox = { x: Math.min(...all.map((p) => p.x)), y: Math.min(...all.map((p) => p.y)), w: 0, h: 0 };
  bbox.w = Math.max(...all.map((p) => p.x + p.w)) - bbox.x;
  bbox.h = Math.max(...all.map((p) => p.y + p.h)) - bbox.y;
  return { pos, edges, bbox };
}

/** A soft S-curve from the parent's right edge to the child's left edge. */
export function edgePath(a, b) {
  const x1 = a.x + a.w; const y1 = a.y + a.h / 2;
  const x2 = b.x; const y2 = b.y + b.h / 2;
  const mx = x1 + (x2 - x1) * 0.5;
  return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
}
